import {
  TELEMETRY_CONTEXT_FIELDS,
  TELEMETRY_ENDPOINT,
  TELEMETRY_EVENT_CONTEXT_RULES,
  TELEMETRY_EVENT_NAMES,
  TELEMETRY_FAILURE_KINDS,
  TELEMETRY_FLIGHT_MODES,
  TELEMETRY_RELEASE_CHANNELS,
  TELEMETRY_SCHEMA_VERSION,
  TELEMETRY_SITE_SLUGS,
  TELEMETRY_SURFACES,
  TELEMETRY_THERMAL_LAYERS,
  TELEMETRY_TOP_LEVEL_FIELDS,
  TELEMETRY_UNIT_PROFILES,
  TELEMETRY_ZOOM_BUCKETS,
  type TelemetryEventName,
  type TelemetryFailureKind,
  type TelemetryFlightMode,
  type TelemetryReleaseChannel,
  type TelemetrySiteSlug,
  type TelemetrySurface,
  type TelemetryThermalLayer,
  type TelemetryUnitProfile,
  type TelemetryZoomBucket
} from "./contract";

export { TELEMETRY_ENDPOINT, TELEMETRY_SCHEMA_VERSION } from "./contract";
export type {
  TelemetryEventName,
  TelemetryFailureKind,
  TelemetryFlightMode,
  TelemetryReleaseChannel,
  TelemetrySiteSlug,
  TelemetrySurface,
  TelemetryThermalLayer,
  TelemetryUnitProfile,
  TelemetryZoomBucket
} from "./contract";

export const MAX_TELEMETRY_BODY_BYTES = 1024;

const MISSING_NUMERIC_VALUE = -1;
const SAFE_RELEASE_VERSION = /^[0-9A-Za-z][0-9A-Za-z._-]{0,31}$/;
const textEncoder = new TextEncoder();

export interface TelemetryContext {
  site_slug?: TelemetrySiteSlug;
  day_offset?: number;
  selected_hour?: number;
  flight_mode?: TelemetryFlightMode;
  unit_profile?: TelemetryUnitProfile;
  thermal_layer?: TelemetryThermalLayer;
  zoom_bucket?: TelemetryZoomBucket;
  failure_kind?: TelemetryFailureKind;
  surface?: TelemetrySurface;
  release_version?: string;
  release_channel?: TelemetryReleaseChannel;
}

export interface TelemetryEventPayload<E extends TelemetryEventName = TelemetryEventName> {
  schema_version: typeof TELEMETRY_SCHEMA_VERSION;
  event: E;
  context: TelemetryContext;
}

export interface TelemetryValidationError {
  code:
    | "body_too_large"
    | "event_not_allowed"
    | "field_value_invalid"
    | "field_value_missing"
    | "shape_invalid"
    | "schema_version_invalid"
    | "top_level_key_unknown"
    | "context_key_unknown";
  message: string;
}

export type TelemetryValidationResult =
  | { ok: true; value: TelemetryEventPayload }
  | { ok: false; error: TelemetryValidationError };

export interface TelemetryDeduper {
  claim(candidate: unknown, nowMs?: number): boolean;
}

export function zoomLevelToBucket(zoom: number): TelemetryZoomBucket {
  if (!Number.isFinite(zoom) || zoom < 9) return "overview";
  if (zoom < 11.5) return "region";
  if (zoom < 13.5) return "site";
  return "house_thermals";
}

export function createTelemetryEvent<E extends TelemetryEventName>(
  event: E,
  context: TelemetryContext
): TelemetryEventPayload<E> | null {
  const result = parseTelemetryEvent({
    schema_version: TELEMETRY_SCHEMA_VERSION,
    event,
    context
  });
  return result.ok ? (result.value as TelemetryEventPayload<E>) : null;
}

export function parseTelemetryEvent(candidate: unknown): TelemetryValidationResult {
  if (!isPlainObject(candidate)) {
    return failure("shape_invalid", "Telemetry payload must be an object.");
  }

  const topLevelKeys = Object.keys(candidate);
  for (const key of topLevelKeys) {
    if (!(TELEMETRY_TOP_LEVEL_FIELDS as readonly string[]).includes(key)) {
      return failure("top_level_key_unknown", `Unknown top-level field "${key}".`);
    }
  }

  if (
    !("schema_version" in candidate) ||
    !("event" in candidate) ||
    !("context" in candidate)
  ) {
    return failure("shape_invalid", "Telemetry payload must include schema_version, event, and context.");
  }

  if (candidate.schema_version !== TELEMETRY_SCHEMA_VERSION) {
    return failure("schema_version_invalid", "Telemetry schema version is not supported.");
  }

  if (
    typeof candidate.event !== "string" ||
    !(TELEMETRY_EVENT_NAMES as readonly string[]).includes(candidate.event)
  ) {
    return failure("event_not_allowed", "Telemetry event is not allowlisted.");
  }

  if (!isPlainObject(candidate.context)) {
    return failure("shape_invalid", "Telemetry context must be an object.");
  }

  const event = candidate.event as TelemetryEventName;
  const contextRule = TELEMETRY_EVENT_CONTEXT_RULES[event];
  const allowedContextKeys = new Set<string>([
    ...contextRule.required,
    ...contextRule.optional
  ]);

  for (const key of Object.keys(candidate.context)) {
    if (!(TELEMETRY_CONTEXT_FIELDS as readonly string[]).includes(key)) {
      return failure("context_key_unknown", `Unknown context field "${key}".`);
    }
    if (!allowedContextKeys.has(key)) {
      return failure(
        "context_key_unknown",
        `Context field "${key}" is not allowed for event "${event}".`
      );
    }
  }

  for (const key of contextRule.required) {
    if (!(key in candidate.context)) {
      return failure(
        "field_value_missing",
        `Required context field "${key}" is missing for event "${event}".`
      );
    }
  }

  const normalizedContext: TelemetryContext = {};
  for (const key of TELEMETRY_CONTEXT_FIELDS) {
    const value = candidate.context[key];
    if (value === undefined) continue;
    const validation = validateContextField(key, value);
    if (!validation.ok) return validation;
    (normalizedContext as Record<string, unknown>)[key] = validation.value;
  }

  const normalizedEvent: TelemetryEventPayload = {
    schema_version: TELEMETRY_SCHEMA_VERSION,
    event,
    context: normalizedContext
  };

  const bodyBytes = serializedTelemetryBodyBytes(normalizedEvent);
  if (bodyBytes > MAX_TELEMETRY_BODY_BYTES) {
    return failure(
      "body_too_large",
      `Telemetry payload exceeds ${MAX_TELEMETRY_BODY_BYTES} bytes.`
    );
  }

  return { ok: true, value: normalizedEvent };
}

export function serializeTelemetryEvent(candidate: unknown): string | null {
  const result = parseTelemetryEvent(candidate);
  if (!result.ok) return null;
  return JSON.stringify(result.value);
}

export function telemetryEventSignature(candidate: unknown): string | null {
  return serializeTelemetryEvent(candidate);
}

export function createTelemetryDeduper(options?: {
  ttlMs?: number;
  maxEntries?: number;
}): TelemetryDeduper {
  const ttlMs = Math.max(250, options?.ttlMs ?? 10_000);
  const maxEntries = Math.max(8, options?.maxEntries ?? 128);
  const seen = new Map<string, number>();

  return {
    claim(candidate: unknown, nowMs = Date.now()): boolean {
      pruneExpiredEntries(seen, nowMs);
      const signature = telemetryEventSignature(candidate);
      if (signature === null) return false;
      const existingExpiry = seen.get(signature);
      if (existingExpiry !== undefined && existingExpiry > nowMs) {
        return false;
      }
      if (seen.size >= maxEntries) {
        const oldestKey = seen.keys().next().value;
        if (typeof oldestKey === "string") {
          seen.delete(oldestKey);
        }
      }
      seen.set(signature, nowMs + ttlMs);
      return true;
    }
  };
}

export function numericTelemetryValue(value: number | undefined): number {
  return value ?? MISSING_NUMERIC_VALUE;
}

function validateContextField(
  key: keyof TelemetryContext,
  value: unknown
):
  | { ok: true; value: TelemetryContext[keyof TelemetryContext] }
  | { ok: false; error: TelemetryValidationError } {
  switch (key) {
    case "site_slug":
      if (
        typeof value === "string" &&
        (TELEMETRY_SITE_SLUGS as readonly string[]).includes(value)
      ) {
        return { ok: true, value };
      }
      return failure(
        "field_value_invalid",
        "site_slug must be one of the canonical public launch slugs."
      );
    case "day_offset":
      return integerField(value, "day_offset", 0, 6);
    case "selected_hour":
      return integerField(value, "selected_hour", 0, 23);
    case "flight_mode":
      return literalField(value, "flight_mode", TELEMETRY_FLIGHT_MODES);
    case "unit_profile":
      return literalField(value, "unit_profile", TELEMETRY_UNIT_PROFILES);
    case "thermal_layer":
      return literalField(value, "thermal_layer", TELEMETRY_THERMAL_LAYERS);
    case "zoom_bucket":
      return literalField(value, "zoom_bucket", TELEMETRY_ZOOM_BUCKETS);
    case "failure_kind":
      return literalField(value, "failure_kind", TELEMETRY_FAILURE_KINDS);
    case "surface":
      return literalField(value, "surface", TELEMETRY_SURFACES);
    case "release_version":
      if (typeof value === "string" && SAFE_RELEASE_VERSION.test(value)) {
        return { ok: true, value };
      }
      return failure(
        "field_value_invalid",
        "release_version must be a bounded release label."
      );
    case "release_channel":
      return literalField(value, "release_channel", TELEMETRY_RELEASE_CHANNELS);
    default:
      return failure("context_key_unknown", `Unknown context field "${key}".`);
  }
}

function integerField(
  value: unknown,
  field: "day_offset" | "selected_hour",
  minimum: number,
  maximum: number
):
  | { ok: true; value: number }
  | { ok: false; error: TelemetryValidationError } {
  if (Number.isInteger(value) && (value as number) >= minimum && (value as number) <= maximum) {
    return { ok: true, value: value as number };
  }
  return failure(
    "field_value_invalid",
    `${field} must be an integer between ${minimum} and ${maximum}.`
  );
}

function literalField<T extends readonly string[]>(
  value: unknown,
  field: string,
  allowed: T
):
  | { ok: true; value: T[number] }
  | { ok: false; error: TelemetryValidationError } {
  if (typeof value === "string" && (allowed as readonly string[]).includes(value)) {
    return { ok: true, value: value as T[number] };
  }
  return failure(
    "field_value_invalid",
    `${field} must be one of ${allowed.join(", ")}.`
  );
}

function pruneExpiredEntries(entries: Map<string, number>, nowMs: number): void {
  for (const [key, expiry] of entries) {
    if (expiry > nowMs) continue;
    entries.delete(key);
  }
}

function serializedTelemetryBodyBytes(candidate: TelemetryEventPayload): number {
  return textEncoder.encode(JSON.stringify(candidate)).length;
}

function isPlainObject(candidate: unknown): candidate is Record<string, unknown> {
  return (
    typeof candidate === "object" &&
    candidate !== null &&
    !Array.isArray(candidate)
  );
}

function failure(
  code: TelemetryValidationError["code"],
  message: string
): { ok: false; error: TelemetryValidationError } {
  return { ok: false, error: { code, message } };
}
